import { FC, useCallback, useState } from "react";
import {
  Box,
  DialogActions,
  DialogContent,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@material-ui/core";
import { closeDrawer } from "actions/drawerActions";
import { useDispatch } from "react-redux";
import { CustomButton } from "componentsNew/buttons/CustomButton";
import { useStyles } from "../../../pages/DaAndFleets/styles";
import { OnChangeEvent } from "types/general";
import { DriverData } from "types/DaAndFleet";
import { daStatusConstants, inactiveReasonConstants } from "constants/constants";
import { MultipleSelectField } from "componentsNew/elements/MultipleSelectField";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { EditDriverData } from "actions/daAndFleetActions";
import { DateTime } from "luxon";
import { SelectApiField } from "componentsNew/elements/SelectApiField";
import api from "api";
import useResize from "hooks/useResize";

interface DaEditDrawerContentProps {
  data: DriverData;
}

const getVanGroups = () => api.getVanGroups();

export const DaEditDrawerContent: FC<DaEditDrawerContentProps> = ({ data }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { width } = useResize();
  const { fleetGroups } = useTypeSelectors((state) => state.dasBoard);
  const { userData } = useTypeSelectors((state) => state.auth);
  const [transporterId, setTransporterId] = useState<string>(data.transporter_id || "");
  const [status, setStatus] = useState<number>(data.status || 0);
  const [statusReason, setStatusReason] = useState<number>(data.status_change_reason || 0);
  const [stations, setStations] = useState<number[]>(
    data.station_spots.map((spot) => spot.station.id),
  );
  const [agency, setAgency] = useState<number | null>(data.station_spots[0]?.agency.id || null);
  const [fleetGroupsList, setFleetGroupsList] = useState<number[]>(
    data.station_spots[0]?.fleet_groups.map((group) => group.id) || [],
  );

  const closenModal = () => dispatch(closeDrawer());

  const handleFilterSubmit = () => {
    dispatch(
      EditDriverData(data.id, {
        transporter_id: transporterId,
        status: status,
        status_change_reason: statusReason,
        stations: stations,
        agency: agency,
        fleet_groups: fleetGroupsList,
      }),
    );
    closenModal();
  };

  const handleChangeAgency = useCallback(
    (agency: number) => {
      setAgency(agency);
    },
    [setAgency],
  );

  const handleChangeStations = useCallback(
    (stations: number[]) => {
      setStations(stations);
    },
    [setStations],
  );

  const handleSelectAllStations = useCallback(() => {
    setStations(() =>
      stations.length !== userData.station_spots.length
        ? userData.station_spots.map((spot) => spot.station.id)
        : [],
    );
  }, [stations.length, userData.station_spots]);

  const handleChangeFleetGroups = useCallback(
    (fleetGroups: number[]) => {
      setFleetGroupsList(fleetGroups);
    },
    [setFleetGroupsList],
  );

  const handleSelectAllFleetGroups = useCallback(() => {
    setFleetGroupsList(() =>
      fleetGroups.length !== fleetGroupsList.length ? fleetGroups.map((group) => group.id) : [],
    );
  }, [fleetGroups, fleetGroupsList.length]);

  return (
    <>
      <DialogContent className={classes.drawerContent}>
        <FormControl fullWidth>
          <Typography className={classes.textareaLabel}>Transporter ID</Typography>
          <OutlinedInput
            value={transporterId}
            onChange={(e: OnChangeEvent) => setTransporterId(e.target.value)}
            disabled={Boolean(data.transporter_id) && data.transporter_id !== "none"}
          />
        </FormControl>
        <FormControl fullWidth className={classes.formSection}>
          <Typography className={classes.textareaLabel}>Status</Typography>
          <Select
            fullWidth
            variant="outlined"
            value={status}
            onChange={(e: OnChangeEvent) => setStatus(e.target.value)}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  width: 840 > width ? 340 : 200,
                },
              },
              variant: "menu",
              getContentAnchorEl: null,
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
              transformOrigin: { vertical: "top", horizontal: "center" },
            }}
          >
            {daStatusConstants.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                <ListItemText primary={status.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth className={classes.formSection}>
          <Typography className={classes.textareaLabel}>Offboarded Reason</Typography>
          <Select
            fullWidth
            variant="outlined"
            value={statusReason}
            onChange={(e: OnChangeEvent) => setStatusReason(e.target.value)}
            renderValue={(value) =>
              status === 5
                ? inactiveReasonConstants.find((reason) => reason.value === value)?.option
                : ""
            }
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  width: 840 > width ? 340 : 200,
                },
              },
              variant: "menu",
              getContentAnchorEl: null,
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
              transformOrigin: { vertical: "top", horizontal: "center" },
            }}
            disabled={status !== 5}
          >
            {inactiveReasonConstants.map((reason) => (
              <MenuItem key={reason.value} value={reason.value}>
                <ListItemText primary={reason.option} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth className={classes.formSection}>
          <Typography className={classes.textareaLabel}>Agency</Typography>
          <SelectApiField
            value={agency}
            onChange={handleChangeAgency}
            api={getVanGroups}
            renderValue={(val: any) => val.name}
          />
        </FormControl>
        <FormControl fullWidth className={classes.formSection}>
          <MultipleSelectField
            options={userData.station_spots.map((spot) => ({
              value: spot.station.id,
              label: spot.station.title,
            }))}
            selected={stations}
            action={handleChangeStations}
            actionAll={handleSelectAllStations}
            label="Stations"
            allLabel="All Stations"
            fullWidth
          />
        </FormControl>
        <FormControl fullWidth className={classes.formSection}>
          <MultipleSelectField
            options={fleetGroups.map((group) => ({
              value: group.id,
              label: group.name,
            }))}
            selected={fleetGroupsList}
            action={handleChangeFleetGroups}
            actionAll={handleSelectAllFleetGroups}
            label="Fleet Vendor"
            allLabel="All Fleet Groups"
            fullWidth
          />
        </FormControl>
      </DialogContent>
      <DialogActions className={classes.actionSection}>
        <Typography className={classes.editedMessage}>
          {data?.last_edited_by
            ? `Last updated by ${data?.last_edited_by} on ${DateTime.fromISO(String(data.last_edit?.split("T0")[0]) || "").toFormat("dd-MM-yyyy")}`
            : ""}
        </Typography>
        <Box className={classes.actionButtons}>
          <Box className={classes.actionButtonsLeft}>
            <CustomButton
              buttonText={"Save"}
              onClick={handleFilterSubmit}
              variant="contained"
              customClass={classes.drawerButton}
            />
            <CustomButton
              buttonText={"Cancel"}
              onClick={closenModal}
              variant="text"
              customClass={classes.drawerButton}
            />
          </Box>
        </Box>
      </DialogActions>
    </>
  );
};
